if (!customElements.get('promotion-container')) {
  customElements.define(
    'promotion-container',
    class PromotionContainer extends HTMLElement {
      constructor() {
        super();
        this.debug = false;
        this.cartDrawer = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.cartPage = document.querySelector('cart-items');
        this.byPassSliderCart = document.querySelector('[data-bypass-slider-cart]') || false;
        this.CHARSET = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
        this.type = this.getAttribute('data-type');
        this.minSpend = this.hasAttribute('data-min-spend') ? this.getAttribute('data-min-spend') : 0;
        this.promotionJson = this.querySelector('script').innerHTML;
        this.promotion = JSON.parse(this.promotionJson || '[]').sort((a, b) => a.v - b.v);
        this.qualifyingCode = this.getAttribute('data-qualifying-code');
        this.initialConfig = this.getAttribute('data-initial-config') == 'true';
        this.triggerVariants = new Set(
          (this.getAttribute('data-trigger-variants') || '')
            .split(',')
            .map((s) => parseInt(s.trim(), 10))
            .filter((n) => !isNaN(n))
        );
        this._reconciling = false;
      }

      /**
       * Lifecycle method called when element is inserted into the DOM.
       * Subscribes to cart update events to monitor cart changes in real-time.
       */
      connectedCallback() {
        subscribe(PUB_SUB_EVENTS.cartUpdate, this.handleCartUpdate.bind(this));
      }

      /**
       * Entry point for promotion handling.
       * Routes to the appropriate handler based on promotion type.
       *
       * @returns {Promise<boolean>} True if promotion was handled successfully, false otherwise
       */
      async handlePromotion() {
        if (!this.promotion.length || !this.type) return false;

        switch (this.type) {
          case 'discountcode_gwp':
            return await this.handleDiscountcodeGWP();
          case 'min_spend':
            return await this.handleMinSpend();
          case 'bxgy':
            return await this.handleBXGY();
          default:
            return false;
        }
      }

      /**
       * Handles minimum spend promotion logic.
       * Checks if cart total (after discounts) meets minimum spend threshold
       * and adds gift item if qualified.
       *
       * @returns {Promise<boolean>} True if gift was added successfully, false otherwise
       */
      async handleMinSpend() {
        try {
          const cart = await fetch('/cart.js');
          const { items, total_price } = await cart.json();

          if (!items.some((item) => item.properties['_free-gift']) && total_price / 100 >= this.minSpend) {
            const promoItem = this.promotion.find((promoItem) => promoItem.x);
            return this.addItem({ item: this.promotion[0].x, discount: null });
          }
        } catch (e) {
          console.error(e);
        }
        return false;
      }

      /**
       * Handles Buy X Get Y promotion logic.
       * Fetches current cart and reconciles the gift line so that its quantity
       * matches the total quantity of qualifying trigger variants in the cart.
       *
       * @returns {Promise<boolean>} True if any cart change was made, false otherwise
       */
      async handleBXGY() {
        try {
          const cart = await fetch('/cart.js');
          const cartData = await cart.json();
          return await this.reconcileBXGY(cartData);
        } catch (e) {
          console.error(e);
        }
        return false;
      }

      /**
       * Reconciles the BXGY gift line against current cart state.
       * Sums quantities of trigger variants and ensures the gift line matches:
       * adds it if missing, updates qty if mismatched, removes it if no triggers remain.
       * Re-entrancy guarded so concurrent cart updates don't double-add.
       *
       * @param {Object} cartData - Cart state with `items` array
       * @returns {Promise<boolean>} True if a cart mutation was issued
       */
      async reconcileBXGY(cartData) {
        if (this._reconciling) return false;
        const giftVariantId = this.promotion[0]?.x;
        if (!giftVariantId) return false;
        const items = cartData.items || [];
        const itemsRemoved = cartData.items_removed || [];

        let desiredQty = 0;
        for (const item of items) {
          if (item.properties && item.properties['_free-gift']) continue;
          if (this.triggerVariants.has(item.variant_id)) {
            desiredQty += item.quantity;
          }
        }

        const giftLineIndex = items.findIndex(
          (item) =>
            item.properties && item.properties['_free-gift'] && item.variant_id === giftVariantId
        );
        const giftLine = giftLineIndex >= 0 ? items[giftLineIndex] : null;

        // If the gift just left the cart while triggers are still present,
        // the customer removed it manually. Honor that for the rest of the
        // session — don't auto-re-add until triggers fully leave and return.
        // Note: Shopify's `items_removed` entries may not preserve line
        // `properties`, so match on variant_id alone. The giftLine lookup
        // above already enforces the `_free-gift` property, so a missing
        // giftLine combined with `variant_id` in items_removed is a reliable
        // signal of customer-initiated removal of our gift line.
        if (desiredQty > 0 && !giftLine) {
          const userRemovedGift = itemsRemoved.some(
            (i) => i && i.variant_id === giftVariantId
          );
          if (userRemovedGift) {
            try {
              sessionStorage.setItem('bxgy_dismissed', '1');
            } catch (e) {
              // sessionStorage can throw in private mode; safe to ignore
            }
            if (this.debug) console.log('bxgy: customer removed gift, dismissing for session');
          }
        }

        const dismissed = (() => {
          try {
            return sessionStorage.getItem('bxgy_dismissed') === '1';
          } catch (e) {
            return false;
          }
        })();

        this._reconciling = true;
        try {
          if (desiredQty === 0) {
            // Cart no longer qualifies — clear any dismissal so the next
            // trigger-add cycle starts fresh.
            try {
              sessionStorage.removeItem('bxgy_dismissed');
            } catch (e) {}
            if (giftLine) {
              if (this.debug) console.log('bxgy: removing gift, no triggers left');
              await this.removeItem(giftLine.key, giftLineIndex);
              return true;
            }
            return false;
          }

          if (dismissed) {
            if (this.debug) console.log('bxgy: dismissed, leaving cart alone');
            return false;
          }

          if (!giftLine) {
            if (this.debug) console.log('bxgy: adding gift x', desiredQty);
            const result = await this.addItem({ item: giftVariantId, quantity: desiredQty });
            return !!result?.success;
          }

          if (giftLine.quantity !== desiredQty) {
            if (this.debug) console.log('bxgy: updating gift qty', giftLine.quantity, '->', desiredQty);
            return await this.updateItemQty(giftLine.key, desiredQty);
          }

          return false;
        } finally {
          this._reconciling = false;
        }
      }

      /**
       * Handles discount code GWP (Gift With Purchase) promotion logic.
       * Gets qualifying discount code, adds gift item if qualified, and publishes
       * cart update or error events.
       *
       * @returns {Promise<boolean>} True if gift was added successfully, false otherwise
       */
      async handleDiscountcodeGWP() {
        try {
          const qualifyingDiscount = await this.getQualifyingDiscount();

          if (qualifyingDiscount) {
            const result = await this.addItem({ discount: qualifyingDiscount });
            if (result.success) {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'promotion',
                productVariantId: result.variant,
                cartData: {
                  ...result.response?.items,
                  sections: result.response?.sections,
                },
              });
              return true;
            } else {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'promotion',
                productVariantId: result.variant,
                errors: result.response?.errors || result.response?.description,
                message: result.response?.message,
              });
            }
          }
        } catch (e) {
          console.error(e);
        }

        return false;
      }

      /**
       * Fetches current cart state and determines if a qualifying discount code exists.
       * Verifies discount code prefix match, minimum spend requirement, and checks
       * if gift is already in cart.
       *
       * @returns {Promise<string|false>} Qualifying discount code if found, false otherwise
       */
      async getQualifyingDiscount() {
        try {
          const cart = await fetch('/cart.js');
          const { items, items_subtotal_price, discount_codes } = await cart.json();
          const [discountAllocation] = discount_codes.filter(
            (discount) => discount.applicable && discount.code.includes(this.qualifyingCode)
          );

          // if cart qualifies and doesnt have a gift already
          if (
            discountAllocation?.code &&
            this.minSpend &&
            items_subtotal_price / 100 >= this.minSpend &&
            !items.some((item) => item.properties['_free-gift'])
          ) {
            return discountAllocation.code;
          }
        } catch (e) {
          console.error(e);
        }

        if (this.debug) console.log('no qualifying discount');
        return false;
      }

      /**
       * Main event handler for cart updates.
       * Determines if cart qualifies for gift, adds gift if qualified and not present,
       * removes gift if not qualified or quantity > 1, and handles removed gift edge cases.
       *
       * @param {Object} event - Cart update event from PUB_SUB_EVENTS.cartUpdate
       * @param {Object} event.cartData - Cart data object containing items, prices, discount codes
       */
      handleCartUpdate(event) {
        if (this.debug) console.log('cart update', event);
        const { cartData } = event;
        if (!cartData || !this.promotion.length) return;

        if (this.type === 'bxgy') {
          this.reconcileBXGY(cartData);
          return;
        }

        const { items, items_removed, items_subtotal_price, total_price, discount_codes } = cartData;
        const giftItemIndex = items?.findIndex((item) => item.properties['_free-gift']);
        let qualifiesForGift = false;

        if (!discount_codes) {
          return;
        }
        const [discountAllocation] = discount_codes.filter(
          (discount) => discount.applicable && discount.code.includes(this.qualifyingCode)
        );

        if (this.type === 'discountcode_gwp') {
          qualifiesForGift = items_subtotal_price / 100 >= this.minSpend && discountAllocation?.code;
        } else if (this.type === 'min_spend') {
          qualifiesForGift = total_price / 100 >= this.minSpend;
        }

        try {
          if (this.type === 'discountcode_gwp' && qualifiesForGift && giftItemIndex < 0) {
            // if cart qualifies and doesn't have a gift already, add it
            const removedGift = items_removed.find(
              (item) =>
                this.promotion.filter((promoItem) => promoItem.z / (promoItem.w + 2) === item.product_id).length > 0
            );
            if (!removedGift) {
              if (this.debug) console.log('cart update adding gift');
              this.addItem({ discount: discountAllocation.code });
            }
          } else if (this.type === 'min_spend' && qualifiesForGift && giftItemIndex < 0) {
            // if cart qualifies and doesn't have a gift already, add it
            const removedGift = items_removed.find(
              (item) => this.promotion.filter((promoItem) => promoItem.z === item.product_id).length > 0
            );
            if (!removedGift) {
              if (this.debug) console.log('cart update adding gift');
              this.addItem({ item: this.promotion[0].x });
            }
          } else if (giftItemIndex >= 0) {
            // if cart doesn't qualify or has too many gifts, remove it
            if (!qualifiesForGift || items[giftItemIndex].quantity > 1) {
              if (this.debug) console.log('cart update removing gift');
              this.removeItem(items[giftItemIndex].key, giftItemIndex);
            }
          }
        } catch (e) {
          console.error(e);
        }
      }

      /**
       * Adds promotion gift to cart.
       * Selects variant based on discount code (GWP) or direct ID (min spend),
       * decodes variant ID for discount code GWP, adds '_free-gift' property,
       * and updates cart drawer/page UI.
       *
       * @param {Object} options - Options object
       * @param {string|null} options.discount - Discount code for GWP promotion (optional)
       * @param {number|null} options.item - Direct variant ID for min spend promotion (optional)
       * @returns {Promise<Object>} Result object with success status, response, and variant ID
       * @returns {boolean} returns.success - True if gift was added successfully
       * @returns {Object} returns.response - Cart response from Shopify API (if successful)
       * @returns {number} returns.variant - Variant ID that was added (if successful)
       */
      async addItem({ discount = null, item = null, quantity = 1 }) {
        if (this.debug) console.log('adding gift to cart', { quantity });
        let promoVariant = null;

        if (item) {
          promoVariant = item;
        } else if (discount) {
          const hash = this.getCodeHash(discount);
          let category = this.getCategoryFromHash(hash);
          let promoItem = this.promotion.find((promoItem) => promoItem.w === category);

          if (!promoItem || !promoItem.y) {
            promoItem = this.promotion.find((promoItem) => promoItem.y);

            if (!promoItem) {
              return { success: false };
            }
          }

          promoVariant = promoItem.x / (promoItem.w + 2);
        } else {
          return { success: false };
        }

        const formData = {};
        formData.items = [
          {
            id: promoVariant,
            quantity: quantity,
            properties: {
              '_free-gift': true,
            },
          },
        ];

        if (this.cartDrawer) {
          formData.sections = this.cartDrawer.getSectionsToRender().map((section) => section.id);
          formData.sections_url = window.location.pathname;
          this.cartDrawer.setActiveElement(document.activeElement);
        }

        if (this.cartPage) {
          formData.sections.push(...this.cartPage.getSectionsToRender().map((section) => section.section));
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        config.body = JSON.stringify(formData);

        try {
          const data = await fetch(window.Shopify.routes.root + 'cart/add.js', config);
          const response = await data.json();

          if (!response.status) {
            if (this.debug) console.log('gift added successfully', response);
            if (this.cartPage) {
              this.cartPage.renderContents(response);
            } else if (this.cartDrawer) {
              this.cartDrawer.renderContents(response, this.byPassSliderCart);
            }
            return { success: true, response: response, variant: promoVariant };
          }
        } catch (e) {
          console.error(e);
        }
        return { success: false };
      }

      /**
       * Removes promotion gift from cart.
       * Disables submit buttons during removal, updates cart via Shopify API,
       * re-enables submit buttons after completion, and updates cart drawer/page UI.
       *
       * @param {string} itemId - Cart item key/ID to remove
       * @param {number} line - Line item index (for error reporting)
       */
      async removeItem(itemId, line) {
        if (this.debug) console.log('remove item', itemId);

        // disable all submit buttons
        const submitBtns = document.querySelectorAll('.cart__ctas button[type="submit"]');
        submitBtns.forEach((btn) => {
          btn.setAttribute('disabled', true);
        });

        const updates = {};
        updates[itemId] = 0;
        const sectionsToRender = this.cartDrawer.getSectionsToRender().map((section) => section.id);

        if (this.cartPage) {
          sectionsToRender.push(...this.cartPage.getSectionsToRender().map((section) => section.section));
        }

        const body = JSON.stringify({
          updates: updates,
          sections: sectionsToRender,
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_update_url}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          body: body,
        })
          .then((response) => response.json())
          .then((parsedState) => {
            if (parsedState.errors) {
              if (this.debug) console.log('errors', parsedState.errors);
              this.updateLiveRegions(line, parsedState.errors);
              return;
            }

            if (this.cartPage) {
              this.cartPage.renderContents(parsedState);
            } else {
              this.cartDrawer.renderContents(parsedState, false, true);
            }
          })
          .catch((error) => {
            console.error('Error updating cart:', error);
          })
          .finally(() => {
            submitBtns.forEach((btn) => {
              btn.removeAttribute('disabled');
            });
          });
      }

      /**
       * Updates an existing cart line's quantity to a specific positive value.
       * Mirrors removeItem's flow but issues a non-zero quantity update so the
       * BXGY gift line can scale up/down as trigger quantities change.
       *
       * @param {string} itemKey - Cart item key/ID to update
       * @param {number} qty - New quantity (positive integer)
       * @returns {Promise<boolean>} True if cart was updated successfully
       */
      async updateItemQty(itemKey, qty) {
        if (this.debug) console.log('update item qty', itemKey, qty);

        const submitBtns = document.querySelectorAll('.cart__ctas button[type="submit"]');
        submitBtns.forEach((btn) => {
          btn.setAttribute('disabled', true);
        });

        const updates = {};
        updates[itemKey] = qty;
        const sectionsToRender = this.cartDrawer
          ? this.cartDrawer.getSectionsToRender().map((section) => section.id)
          : [];

        if (this.cartPage) {
          sectionsToRender.push(...this.cartPage.getSectionsToRender().map((section) => section.section));
        }

        const body = JSON.stringify({
          updates: updates,
          sections: sectionsToRender,
          sections_url: window.location.pathname,
        });

        try {
          const response = await fetch(`${routes.cart_update_url}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: body,
          });
          const parsedState = await response.json();

          if (parsedState.errors) {
            if (this.debug) console.log('updateItemQty errors', parsedState.errors);
            return false;
          }

          if (this.cartPage) {
            this.cartPage.renderContents(parsedState);
          } else if (this.cartDrawer) {
            this.cartDrawer.renderContents(parsedState, false, true);
          }
          return true;
        } catch (error) {
          console.error('Error updating cart line:', error);
          return false;
        } finally {
          submitBtns.forEach((btn) => {
            btn.removeAttribute('disabled');
          });
        }
      }

      /**
       * Maps hash value to gift category (0-4).
       * Initial config mode uses predefined character sets, standard mode uses numeric ranges.
       *
       * @param {string|number} hash - Hash value from discount code (string for initial config, number for standard)
       * @returns {number} Category index (0-4)
       */
      getCategoryFromHash(hash) {
        if (this.initialConfig) {
          const lookup = [
            new Set(['T', 'J', '4', 'C', '3', 'S']),
            new Set(['Y', 'A', 'D', '5', 'R', 'Z', 'N']),
            new Set(['F', 'W', 'V', 'Q', 'G', 'L', 'B']),
            new Set(['9', 'P', 'H', 'E', '7', '2']),
            new Set(['U', '6', 'M', 'X', '`K', '8']),
          ];
          return lookup.findIndex((set) => set.has(hash));
        } else {
          if (hash < 3) return 0;
          if (hash < 6) return 1;
          if (hash < 10) return 2;
          if (hash < 50) return 3;
          return 4;
        }
      }

      /**
       * Calculates hash from discount code.
       * Initial config mode returns 5th character directly.
       * Standard mode hashes last 3 characters using CHARSET.
       *
       * @param {string} code - Discount code string
       * @returns {string|number} Hash value (string for initial config, number for standard mode)
       */
      getCodeHash(code) {
        const last3 = code.slice(-3);
        const fifth = code[4];
        let hash = 0;

        if (this.initialConfig) {
          return fifth;
        } else {
          for (let i = 0; i < last3.length; i++) {
            const char = last3[i];
            const charIndex = this.CHARSET.indexOf(char);
            if (charIndex !== -1) {
              hash = (hash * 32 + charIndex) % 100;
            }
          }
        }
        return hash;
      }

      /**
       * Utility to extract HTML from section response.
       * Parses HTML string and queries selector to get innerHTML.
       *
       * @param {string} html - HTML string to parse
       * @param {string} selector - CSS selector to query
       * @returns {string} InnerHTML of the selected element
       */
      getSectionInnerHTML(html, selector) {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      }
    }
  );
}
